import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Avatar,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Skeleton,
    Tabs,
    Tab,
    Paper,
    IconButton,
    Snackbar
} from '@mui/material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HomeIcon from '@mui/icons-material/Home';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PersonIcon from '@mui/icons-material/Person';
import { StaffService } from '../api/services/staffService';
import DeactivateStaffModal from './DeactivateStaffModal';
import AddNoteModal from './AddNoteModal';

export default function StaffDetailsModal({ open, onClose, staffId, onEdit, initialTab = 0, onSuccess }) {
    const [staff, setStaff] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(0);
    const [performanceNotes, setPerformanceNotes] = useState([]);
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [notesError, setNotesError] = useState('');
    const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
    
    // Void performance note states
    const [isVoidingConfirmOpen, setIsVoidingConfirmOpen] = useState(false);
    const [noteToVoid, setNoteToVoid] = useState(null);
    const [isVoiding, setIsVoiding] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const { user: currentUser } = useSelector((state) => state.auth);
    const normalizeRole = (role) => {
        const roleName = typeof role === 'object' && role !== null ? role.name : role;
        return String(roleName || '').replace(/[_\s-]+/g, '').toUpperCase();
    };
    const userRole = normalizeRole(currentUser?.role);
    const canVoid = ['SUPERADMIN', 'HOTELADMIN', 'ADMIN', 'MANAGER', 'HR'].includes(userRole);

    useEffect(() => {
        if (open && staffId) {
            setTab(initialTab);
            setPerformanceNotes([]);
            fetchStaffDetails();
        }
    }, [open, staffId, initialTab]);

    useEffect(() => {
        if (tab === 1 && staffId && performanceNotes.length === 0 && !isLoadingNotes) {
            fetchPerformanceNotes();
        }
    }, [tab, staffId]);

    const fetchStaffDetails = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await StaffService.getStaffById(staffId);
            setStaff(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch staff details.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPerformanceNotes = async () => {
        setIsLoadingNotes(true);
        setNotesError('');
        try {
            const data = await StaffService.getStaffNotes(staffId);
            setPerformanceNotes(Array.isArray(data) ? data : []);
        } catch (err) {
            setNotesError(err.message || 'Failed to load performance notes.');
        } finally {
            setIsLoadingNotes(false);
        }
    };

    const handleEditClick = () => {
        if (staff) {
            onEdit(staff);
            onClose();
        }
    };

    const handleNoteSuccess = () => {
        // Force re-fetch of notes after adding
        setPerformanceNotes([]);
        fetchPerformanceNotes();
    };

    const handleOpenVoidConfirm = (note) => {
        setNoteToVoid(note);
        setIsVoidingConfirmOpen(true);
    };

    const handleConfirmVoid = async () => {
        if (!noteToVoid) return;
        setIsVoiding(true);
        try {
            await StaffService.voidStaffNote(noteToVoid.id || noteToVoid._id);
            
            // Optimistically update local state: mark as Voided
            setPerformanceNotes(prev =>
                prev.map(n => (n.id === noteToVoid.id || n._id === noteToVoid._id)
                    ? { ...n, status: 'Voided', isVoided: true }
                    : n
                )
            );
            
            setToast({ open: true, message: 'Performance note successfully voided', severity: 'success' });
            setIsVoidingConfirmOpen(false);
            setNoteToVoid(null);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to void performance note.';
            setToast({ open: true, message: msg, severity: 'error' });
        } finally {
            setIsVoiding(false);
        }
    };

    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
            <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.3 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    position: 'relative'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        {isLoading ? (
                            <Skeleton variant="circular" width={64} height={64} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                        ) : (
                            <Avatar sx={{ width: 64, height: 64, fontSize: '1.5rem', bgcolor: 'white', color: 'primary.main', fontWeight: 'bold', border: '3px solid rgba(255,255,255,0.3)' }}>
                                {(staff?.fullName || staff?.name || 'S').charAt(0)}
                            </Avatar>
                        )}
                        <Box>
                            {isLoading ? (
                                <>
                                    <Skeleton width={200} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                                    <Skeleton width={150} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                                </>
                            ) : (
                                <>
                                    <Typography variant="h5" fontWeight="bold">
                                        {staff?.fullName || staff?.name || 'Unknown Staff'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {staff?.designation || 'Staff Member'} • {staff?.department?.name || staff?.department || 'General'}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, minHeight: 400 }}>
                {error ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Alert severity="error" variant="outlined">{error}</Alert>
                        <Button onClick={fetchStaffDetails} sx={{ mt: 2 }}>Retry</Button>
                    </Box>
                ) : (
                    <>
                        <Tabs 
                            value={tab} 
                            onChange={(e, v) => setTab(v)} 
                            sx={{ borderBottom: 1, borderColor: '#eaecf0', px: 2, bgcolor: 'grey.50' }}
                        >
                            <Tab label="Personal Profile" />
                            <Tab label="Performance Notes" />
                        </Tabs>

                        <Box sx={{ p: 3 }}>
                            {tab === 0 && (
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                                            Employment Details
                                        </Typography>
                                        <Divider sx={{ mb: 2.5 }} />
                                        {isLoading ? (
                                            <Box sx={{ py: 1 }}>{[1, 2, 3, 4].map(i => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)}</Box>
                                        ) : (
                                            <>
                                                <InfoItem icon={<BadgeIcon />} label="Employee ID" value={staff?.employeeId} />
                                                <InfoItem icon={<WorkIcon />} label="Employment Type" value={staff?.employmentType || staff?.type} />
                                                <InfoItem icon={<CalendarMonthIcon />} label="Joining Date" value={staff?.joiningDate ? new Date(staff.joiningDate).toLocaleDateString() : 'N/A'} />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>ACCOUNT STATUS</Typography>
                                                    <Chip 
                                                        label={staff?.status || 'Active'} 
                                                        size="small" 
                                                        color={staff?.status === 'Active' ? 'success' : 'default'} 
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                                            Contact Information
                                        </Typography>
                                        <Divider sx={{ mb: 2.5 }} />
                                        {isLoading ? (
                                            <Box sx={{ py: 1 }}>{[1, 2, 3, 4].map(i => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)}</Box>
                                        ) : (
                                            <>
                                                <InfoItem icon={<EmailIcon />} label="Email Address" value={staff?.contactInfo?.email} />
                                                <InfoItem icon={<PhoneIcon />} label="Phone Number" value={staff?.contactInfo?.phone} />
                                                <InfoItem icon={<ContactEmergencyIcon />} label="Emergency Contact" value={staff?.contactInfo?.emergencyContact} />
                                                <InfoItem icon={<HomeIcon />} label="Address" value={staff?.address} />
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                            {/* Performance Notes Tab */}
                            {tab === 1 && (
                                <Box>
                                    {/* Tab toolbar */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<NoteAltIcon />}
                                            onClick={() => setIsAddNoteOpen(true)}
                                            disabled={!staff}
                                        >
                                            Add Note
                                        </Button>
                                    </Box>
                                    {isLoadingNotes ? (
                                        [...Array(3)].map((_, i) => (
                                            <Paper key={i} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Skeleton width="40%" height={22} />
                                                    <Skeleton width="20%" height={22} />
                                                </Box>
                                                <Skeleton width="100%" height={18} />
                                                <Skeleton width="80%" height={18} />
                                                <Skeleton width="25%" height={18} sx={{ mt: 1 }} />
                                            </Paper>
                                        ))
                                    ) : notesError ? (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {notesError}
                                            <Button size="small" onClick={fetchPerformanceNotes} sx={{ ml: 2 }}>Retry</Button>
                                        </Alert>
                                    ) : performanceNotes.length > 0 ? (
                                        performanceNotes.map((note, idx) => {
                                            const isNoteVoided = note.status === 'Voided' || note.isVoided;
                                            return (
                                                <Paper
                                                    key={note.id || note._id || idx}
                                                    variant="outlined"
                                                    sx={{ 
                                                        p: 2.5, 
                                                        mb: 2, 
                                                        borderRadius: 2, 
                                                        borderLeft: '4px solid', 
                                                        borderLeftColor: isNoteVoided ? 'grey.400' : 'primary.main',
                                                        opacity: isNoteVoided ? 0.6 : 1,
                                                        bgcolor: isNoteVoided ? 'action.hover' : 'inherit'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <NoteAltIcon fontSize="small" color={isNoteVoided ? 'disabled' : 'primary'} />
                                                            {(note.category || note.tag || note.type) && (
                                                                <Chip label={note.category || note.tag || note.type} size="small" color={isNoteVoided ? 'default' : 'primary'} variant="outlined" />
                                                            )}
                                                            {isNoteVoided && (
                                                                <Chip label="Voided" size="small" color="error" variant="filled" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {note.createdAt
                                                                    ? new Date(note.createdAt).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
                                                                    : note.date || '--'}
                                                            </Typography>
                                                            {canVoid && !isNoteVoided && (
                                                                <Button
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleOpenVoidConfirm(note)}
                                                                    sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontSize: '0.75rem', fontWeight: 'bold' }}
                                                                >
                                                                    Void
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                                                    {note.content || note.note || note.description || '—'}
                                                </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {note.author?.fullName || note.authorName || note.author || 'Manager'}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            );
                                        })
                                    ) : (
                                        <Box sx={{ py: 8, textAlign: 'center' }}>
                                            <NoteAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="body1" color="text.secondary">No performance notes recorded for this employee yet.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
                <Button 
                    onClick={() => setIsDeactivateOpen(true)} 
                    color="error" 
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    disabled={isLoading || !staff || staff.status === 'Inactive'}
                >
                    Deactivate Staff
                </Button>
                <Box>
                    <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>Close</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleEditClick}
                        disabled={isLoading || !staff}
                    >
                        Edit Profile
                    </Button>
                </Box>
            </DialogActions>

            <DeactivateStaffModal
                open={isDeactivateOpen}
                onClose={() => setIsDeactivateOpen(false)}
                staff={staff}
                onSuccess={() => {
                    if (onSuccess) onSuccess('Staff deactivated successfully');
                    onClose();
                }}
            />

            <AddNoteModal
                open={isAddNoteOpen}
                onClose={() => setIsAddNoteOpen(false)}
                onSuccess={handleNoteSuccess}
                staffId={staffId}
                staffName={staff?.fullName || staff?.name}
            />

            {/* Void Note Confirmation Dialog */}
            <Dialog open={isVoidingConfirmOpen} onClose={isVoiding ? undefined : () => setIsVoidingConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Void Performance Note</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to void this performance note? This will mark it as invalid and remove it from active evaluations.
                    </Typography>
                    {noteToVoid && (
                        <Box sx={{ mt: 2, bgcolor: 'grey.50', borderRadius: 2, p: 2, borderLeft: '3px solid #d32f2f' }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                "{noteToVoid.content || noteToVoid.note || noteToVoid.description || '—'}"
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={() => setIsVoidingConfirmOpen(false)} disabled={isVoiding}>Keep Note</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmVoid}
                        disabled={isVoiding}
                        startIcon={isVoiding ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isVoiding ? 'Voiding...' : 'Yes, Void Note'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Local Alert Snackbar */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
